// src/components/routes/Other/AgentStats/AgentStatsReports.js

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import api from '../../../../utils/api';
import { buildAgentStatusByExtension } from '../../../../utils/agentAvailability';
import './AgentStatsReports.css';

const formatSeconds = (value) => {
    const totalSeconds = Number(value || 0);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
};

const formatDateTime = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleString('en-US', {
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const parseStateReport = (value) => {
    if (!value) return {};
    if (typeof value === 'object') return value;
    try {
        return JSON.parse(value);
    } catch (error) {
        return {};
    }
};

const STATE_VARIANTS = {
    'On Call': 'oncall',
    Idle: 'idle',
    'Wrap Up': 'wrapup',
    Hold: 'hold',
    'Not Available': 'notavailable'
};

const buildVisibleStates = (report) => {
    const detailedStates = parseStateReport(report.not_available_detailed_report);
    const visibleStates = {};

    Object.entries(detailedStates).forEach(([state, seconds]) => {
        if (Number(seconds || 0) > 0) {
            visibleStates[state] = seconds;
        }
    });

    const fallbackStates = {
        'On Call': report.on_call_time,
        Idle: report.idle_time,
        'Wrap Up': report.wrap_up_time,
        Hold: report.hold_time,
        'Not Available': report.not_available_time
    };

    Object.entries(fallbackStates).forEach(([state, seconds]) => {
        if (Number(seconds || 0) > 0 && visibleStates[state] === undefined) {
            visibleStates[state] = seconds;
        }
    });

    return visibleStates;
};

const buildActivitySegments = (stateReport) => {
    const total = Object.values(stateReport).reduce((sum, seconds) => sum + Number(seconds || 0), 0);
    if (total <= 0) return [];

    return Object.entries(stateReport).map(([state, seconds]) => ({
        state,
        seconds: Number(seconds || 0),
        percent: (Number(seconds || 0) / total) * 100,
        variant: STATE_VARIANTS[state] || 'other'
    }));
};

const AgentStatsReports = () => {
    const [reports, setReports] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const token = localStorage.getItem('token');
    const tokenData = useMemo(() => {
        if (!token) return null;
        try {
            return jwtDecode(token);
        } catch (error) {
            return null;
        }
    }, [token]);

    const fetchReports = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const params = { limit: 300 };

            if (
                tokenData?.business_center_id &&
                ['business_admin', 'receptionist'].includes(tokenData.role)
            ) {
                params.businessCenterId = tokenData.business_center_id;
            }

            const response = await api.get('/agent-stats/final', { params });

            setReports(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            const status = error.response?.status;
            const backendMessage = error.response?.data?.message || error.response?.data?.error;
            setError(
                backendMessage
                    ? `${status ? `${status}: ` : ''}${backendMessage}`
                    : 'Unable to fetch agent stats reports. Please confirm the backend is running and restarted.'
            );
        } finally {
            setLoading(false);
        }
    }, [tokenData?.business_center_id, tokenData?.role]);

    const refreshReports = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            const businessCenterId = tokenData?.business_center_id;
            await api.post('/agent-stats/poll-now', businessCenterId ? { businessCenterId } : {});
            await fetchReports();
        } catch (error) {
            const status = error.response?.status;
            const backendMessage = error.response?.data?.message || error.response?.data?.error;
            setError(
                backendMessage
                    ? `${status ? `${status}: ` : ''}${backendMessage}`
                    : 'Unable to refresh agent stats from the API.'
            );
            setLoading(false);
        }
    }, [fetchReports, tokenData?.business_center_id]);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    const latestByExtension = useMemo(() => {
        const latest = new Map();
        reports.forEach((report) => {
            const key = report.extension || report.id;
            if (!latest.has(key)) {
                latest.set(key, report);
            }
        });
        return Array.from(latest.values());
    }, [reports]);

    const statusByExtension = useMemo(() => (
        buildAgentStatusByExtension(reports)
    ), [reports]);

    const isReportAvailable = useCallback((report) => (
        statusByExtension[String(report.extension || '').trim()] === 'available'
    ), [statusByExtension]);

    const totals = useMemo(() => {
        return latestByExtension.reduce((summary, report) => ({
            agents: summary.agents + 1,
            available: summary.available + (isReportAvailable(report) ? 1 : 0),
            calls: summary.calls + Number(report.total_calls || 0),
            answered: summary.answered + Number(report.answered_calls || 0)
        }), {
            agents: 0,
            available: 0,
            calls: 0,
            answered: 0
        });
    }, [latestByExtension, isReportAvailable]);

    const answeredRate = totals.calls > 0 ? Math.round((totals.answered / totals.calls) * 100) : 0;

    const lastUpdatedAt = useMemo(() => {
        return latestByExtension.reduce((latest, report) => {
            const time = report.fetched_at ? new Date(report.fetched_at).getTime() : 0;
            return time > latest ? time : latest;
        }, 0);
    }, [latestByExtension]);

    const filteredAgents = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        const matches = latestByExtension.filter((report) => {
            if (!term) return true;
            const haystack = [
                report.registered_agent_name,
                report.agent_name,
                report.extension,
                report.registered_company_name,
                report.business_name,
                report.tenant_name
            ].filter(Boolean).join(' ').toLowerCase();
            return haystack.includes(term);
        });

        return [...matches].sort((a, b) => {
            const aAvailable = isReportAvailable(a);
            const bAvailable = isReportAvailable(b);
            if (aAvailable !== bAvailable) return aAvailable ? -1 : 1;
            return Number(b.total_calls || 0) - Number(a.total_calls || 0);
        });
    }, [latestByExtension, searchTerm, isReportAvailable]);

    return (
        <div className="agent-report-page">
            <div className="agent-report-toolbar">
                <div className="agent-report-toolbar-title">
                    <h1>Agent Stats Reports</h1>
                    {lastUpdatedAt > 0 && (
                        <span className="agent-report-updated">
                            <i className="fas fa-clock"></i> Updated {formatDateTime(lastUpdatedAt)}
                        </span>
                    )}
                </div>

                <div className="agent-report-search">
                    <i className="fas fa-search agent-report-search-icon"></i>
                    <input
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        placeholder="Search by agent, extension, or company"
                        aria-label="Search agents"
                    />
                    {searchTerm && (
                        <button
                            type="button"
                            className="agent-report-search-clear"
                            aria-label="Clear search"
                            onClick={() => setSearchTerm('')}
                        >
                            <i className="fas fa-times"></i>
                        </button>
                    )}
                </div>

                <button type="button" className="agent-report-refresh-btn" onClick={refreshReports} disabled={loading}>
                    <i className={`fas fa-rotate${loading ? ' fa-spin' : ''}`}></i>
                    {loading ? 'Refreshing...' : 'Refresh'}
                </button>
            </div>

            {error && <div className="agent-report-error">{error}</div>}

            <div className="agent-report-summary">
                <div>
                    <span>Agents</span>
                    <strong>{totals.agents}</strong>
                </div>
                <div>
                    <span>Available Now</span>
                    <strong className="agent-report-summary-available">{totals.available}</strong>
                </div>
                <div>
                    <span>Total Calls</span>
                    <strong>{totals.calls}</strong>
                </div>
                <div>
                    <span>Answered Rate</span>
                    <strong>{answeredRate}%</strong>
                </div>
            </div>

            {loading && reports.length === 0 ? (
                <div className="agent-report-loading">
                    <span className="agent-report-spinner"></span>
                    <p>Loading agent stats...</p>
                </div>
            ) : filteredAgents.length === 0 ? (
                <div className="agent-report-empty">
                    <i className="fas fa-user-slash"></i>
                    <p>{searchTerm ? 'No agents match your search.' : 'No agent stats recorded yet.'}</p>
                </div>
            ) : (
                <div className="agent-report-card-grid">
                    {filteredAgents.map((report) => {
                        const stateReport = buildVisibleStates(report);
                        const activitySegments = buildActivitySegments(stateReport);
                        const isAvailable = isReportAvailable(report);

                        return (
                            <article
                                className={`agent-report-card ${isAvailable ? 'agent-report-card-available' : ''}`}
                                key={`${report.extension}-${report.fetched_at}`}
                            >
                                <div className="agent-report-card-top">
                                    <div>
                                        <h2>{report.registered_agent_name || report.agent_name || 'Unknown Agent'}</h2>
                                        <span>{report.registered_company_name || report.business_name || report.tenant_name}</span>
                                    </div>
                                    <div className="agent-report-card-top-meta">
                                        <strong>{report.extension}</strong>
                                        <span className={`agent-report-badge agent-report-badge--${isAvailable ? 'available' : 'unavailable'}`}>
                                            <span className="agent-report-badge-dot"></span>
                                            {isAvailable ? 'Available' : 'Not Available'}
                                        </span>
                                    </div>
                                </div>
                                <div className="agent-report-metrics">
                                    <span>Calls <b>{report.total_calls || 0}</b></span>
                                    <span>Idle <b>{formatSeconds(report.idle_time)}</b></span>
                                    <span>On Call <b>{formatSeconds(report.on_call_time)}</b></span>
                                    {Number(report.not_available_time || 0) > 0 && (
                                        <span>NA <b>{formatSeconds(report.not_available_time)}</b></span>
                                    )}
                                </div>
                                {activitySegments.length > 0 && (
                                    <div className="agent-report-bar" role="img" aria-label="Time breakdown by state">
                                        {activitySegments.map(({ state, percent, variant }) => (
                                            <span
                                                key={state}
                                                className={`agent-report-bar-segment agent-report-bar-segment--${variant}`}
                                                style={{ width: `${percent}%` }}
                                                title={`${state}: ${formatSeconds(stateReport[state])}`}
                                            />
                                        ))}
                                    </div>
                                )}
                                <div className="agent-report-states">
                                    {Object.keys(stateReport).length > 0 ? (
                                        Object.entries(stateReport).map(([state, seconds]) => (
                                            <span
                                                key={state}
                                                className={`agent-report-state-chip agent-report-state-chip--${STATE_VARIANTS[state] || 'other'}`}
                                            >
                                                {state}: {formatSeconds(seconds)}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="agent-report-state-chip agent-report-state-chip--empty">No state breakdown</span>
                                    )}
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default AgentStatsReports;
